import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Booking, Flight, Hotel } from '../types';

interface ReminderItem {
  id: string;
  title: string;
  subtitle?: string;
  reminderMinutes: number;
  /** Full ISO datetime string of the event */
  eventTime: string;
  /** The closing end of a booking that spans days, rather than its start. */
  isEnd?: boolean;
}

function flightToItems(f: Flight): ReminderItem[] {
  return (f.stops ?? [])
    .filter(s => s.reminderMinutes)
    .map(s => ({
      id: `flight-${f.id}-stop-${s.id}`,
      title: `${s.fromAirport} → ${s.toAirport}${s.flightNumber ? ` · ${s.flightNumber}` : ''}`,
      reminderMinutes: s.reminderMinutes!,
      eventTime: s.departureTime,
    }));
}

function hotelToItem(h: Hotel, checkInLabel: string): ReminderItem | null {
  if (!h.reminderMinutes) return null;
  return { id: `hotel-${h.id}`, title: `${checkInLabel}: ${h.name}`, subtitle: h.address ?? undefined, reminderMinutes: h.reminderMinutes, eventTime: h.checkIn };
}

/**
 * One reminder per end of the booking. A booking that spans days is given back
 * as well as picked up, and the drop-off is often the easiest to forget (a car
 * due at 06:30), so the same lead time is applied to both moments.
 */
function bookingToItems(b: Booking, endLabel: string): ReminderItem[] {
  if (!b.reminderMinutes) return [];
  const at = (day: string, time: string) => {
    const eventTime = `${day.slice(0, 10)}T${time}:00`;
    return isNaN(new Date(eventTime).getTime()) ? null : eventTime;
  };

  const items: ReminderItem[] = [];
  const start = b.startTime ? at(b.date, b.startTime) : null;
  if (start) {
    items.push({ id: `booking-${b.id}`, title: b.title, subtitle: b.venue ?? undefined, reminderMinutes: b.reminderMinutes, eventTime: start });
  }

  const spansDays = !!b.endDate && b.endDate.slice(0, 10) > b.date.slice(0, 10);
  const end = spansDays && b.endTime ? at(b.endDate!, b.endTime) : null;
  if (end) {
    items.push({ id: `booking-${b.id}-end`, title: endLabel, subtitle: b.venue ?? undefined, reminderMinutes: b.reminderMinutes, eventTime: end, isEnd: true });
  }
  return items;
}

function fmtDelay(minutes: number) {
  return minutes >= 60 ? `${minutes / 60}h` : `${minutes} min`;
}

export function useBookingReminders(
  flights: Flight[],
  hotels: Hotel[],
  bookings: Booking[],
) {
  const { t } = useTranslation();
  const timers = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  const [permission, setPermission] = useState<NotificationPermission>(
    typeof Notification !== 'undefined' ? Notification.permission : 'denied',
  );

  async function requestPermission() {
    if (typeof Notification === 'undefined') return;
    const result = await Notification.requestPermission();
    setPermission(result);
  }

  useEffect(() => {
    if (typeof Notification !== 'undefined' && Notification.permission === 'default') {
      Notification.requestPermission().then(p => setPermission(p));
    }
  }, []);

  useEffect(() => {
    timers.current.forEach(t => clearTimeout(t));
    timers.current.clear();

    if (typeof Notification === 'undefined') return;

    const items: ReminderItem[] = [
      ...flights.flatMap(flightToItems),
      ...hotels.map(h => hotelToItem(h, t('bookings.form.checkIn'))),
      ...bookings.flatMap(b => bookingToItems(b, t('bookings.reminder.endTitle', { title: b.title }))),
    ].filter(Boolean) as ReminderItem[];

    const now = Date.now();

    for (const item of items) {
      const eventMs = new Date(item.eventTime).getTime();
      if (isNaN(eventMs)) continue;

      const reminderMs = eventMs - item.reminderMinutes * 60 * 1000;
      const delay = reminderMs - now;
      if (delay < 0) continue;

      const timer = setTimeout(() => {
        if (Notification.permission !== 'granted') return;
        new Notification(`⏰ ${item.title}`, {
          body: [
            t(item.isEnd ? 'bookings.reminder.endsIn' : 'bookings.reminder.startsIn', { delay: fmtDelay(item.reminderMinutes) }),
            item.subtitle,
          ].filter(Boolean).join(' · '),
          icon: '/favicon-32.png',
          tag: `reminder-${item.id}`,
        });
      }, delay);

      timers.current.set(item.id, timer);
    }

    return () => {
      timers.current.forEach(t => clearTimeout(t));
      timers.current.clear();
    };
  }, [flights, hotels, bookings, t]);

  return { permission, requestPermission };
}
